/**
 * The grid both Player Stats views render into.
 *
 * Built straight on `DataGridPremium` with *declared* columns rather than reusing
 * `Elements/ExplorerDataGrid`: that component infers its schema from arbitrary
 * query results and carries filter-context cells, video launchers and custom
 * formula columns. This page is the opposite — a fixed, grouped, registry-driven
 * schema — so it shares the percentile chip and nothing else.
 */

import { useMemo } from "react";
import { Box } from "@mui/material";
import {
  DataGridPremium,
  GridColDef,
  gridExpandedSortedRowIdsSelector,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";

import PercentileCell from "../../Elements/PercentileCell";
import { PlayerStatColumn } from "../../../models/PlayerStats";
import {
  GROUP_END_CLASS,
  StatRowData,
  buildColumnGroups,
  buildStatColumns,
  formatStat,
} from "./columns";
import PlayerStatsToolbar, {
  EfficiencyMode,
  PercentileMode,
} from "./PlayerStatsToolbar";

/** Marks the group-header cell above the pinned identity block, which holds a
 *  control rather than a label. */
const IDENTITY_GROUP_CLASS = "player-stats-identity-group";

export interface PlayerStatsGridProps<T extends StatRowData> {
  rows: T[];
  columns: PlayerStatColumn[];
  /** Columns pinned to the left — player identity, or season identity. */
  identityColumns: GridColDef[];
  getRowId: (row: T) => string;
  loading?: boolean;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  percentileMode: PercentileMode;
  onPercentileModeChange: (mode: PercentileMode) => void;
  /** Percentiles keyed by row id, used when the mode is "view". */
  viewPercentiles?: Record<string, Record<string, number>>;
  onOpenFilters?: (anchor: HTMLElement) => void;
  filtersOpen?: boolean;
  /** Shown as a badge on the Filters button. */
  filterCount?: number;
  efficiencyMode?: EfficiencyMode;
  onEfficiencyModeChange?: (mode: EfficiencyMode) => void;
  percentileModes?: PercentileMode[];
  /** Extra class per row — used to mark career rows on the per-player view. */
  getRowClassName?: (row: T) => string | undefined;
  /** Rendered in the group-header row above the pinned identity block, which is
   *  otherwise empty. The league table puts its player search there. */
  identityHeader?: React.ReactNode;
  exportFileName: string;
}

export default function PlayerStatsGrid<T extends StatRowData>({
  rows,
  columns,
  identityColumns,
  getRowId,
  loading,
  sortModel,
  onSortModelChange,
  percentileMode,
  onPercentileModeChange,
  viewPercentiles,
  onOpenFilters,
  filtersOpen,
  filterCount,
  percentileModes,
  efficiencyMode,
  onEfficiencyModeChange,
  getRowClassName,
  identityHeader,
  exportFileName,
}: PlayerStatsGridProps<T>) {
  const apiRef = useGridApiRef();

  const gridColumns = useMemo(() => {
    const byKey = new Map(columns.map((c) => [c.key, c]));

    const renderCell: GridColDef["renderCell"] = (params) => {
      const column = byKey.get(params.field);
      if (!column) return null;
      const row = params.row as StatRowData;

      if (column.format === "text") {
        return <span>{formatStat(row.t?.[column.key], column.format)}</span>;
      }

      const value = row.v?.[column.key];
      const percentile =
        percentileMode === "view"
          ? viewPercentiles?.[String(params.id)]?.[column.key]
          : row.p?.[column.key];

      return (
        <PercentileCell
          value={formatStat(value, column.format)}
          percentile={percentileMode === "off" ? null : percentile ?? null}
        />
      );
    };

    // The identity block is one unit, so only its outer edge gets a divider —
    // marked here rather than by the caller so every view gets it the same way.
    const identity = identityColumns.map((column, index) =>
      index === identityColumns.length - 1
        ? {
            ...column,
            cellClassName: GROUP_END_CLASS,
            headerClassName: GROUP_END_CLASS,
          }
        : column,
    );

    return [...identity, ...buildStatColumns(columns, renderCell)];
  }, [columns, identityColumns, percentileMode, viewPercentiles]);

  const columnGroupingModel = useMemo(() => {
    const groups = buildColumnGroups(columns);
    if (!identityHeader || !identityColumns.length) return groups;
    return [
      {
        groupId: "identity",
        headerAlign: "left" as const,
        headerClassName: IDENTITY_GROUP_CLASS,
        renderHeaderGroup: () => <>{identityHeader}</>,
        children: identityColumns.map((c) => ({ field: c.field })),
      },
      ...groups,
    ];
  }, [columns, identityColumns, identityHeader]);

  const pinnedLeft = useMemo(
    () => identityColumns.map((c) => c.field),
    [identityColumns],
  );

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <DataGridPremium
        apiRef={apiRef}
        rows={rows}
        columns={gridColumns}
        getRowId={getRowId as (row: any) => string}
        loading={loading}
        // Floor is the identity cell: a 32px headshot and two lines of text.
        // Anything shorter clips them.
        rowHeight={40}
        columnHeaderHeight={34}
        // The group row is a label strip, not a band — it carries the search box
        // but shouldn't be sized by it, so the input is shrunk to fit rather than
        // the row grown to hold it.
        columnGroupHeaderHeight={26}
        hideFooter
        disableAggregation
        disableRowGrouping
        disableRowSelectionOnClick
        disableDensitySelector
        // Nothing in the per-column menu applies here — filtering is the page's
        // own panel, visibility is the toolbar, and the columns aren't groupable.
        // Removing it also stops its icon from reserving width on hover.
        disableColumnMenu
        // One filtering mechanism only. The page's own filter panel drives the
        // visible rows, and "view" percentiles are ranked over exactly that set;
        // a second, invisible filter model would make those percentiles lie.
        disableColumnFilter
        columnGroupingModel={columnGroupingModel}
        sortingOrder={["desc", "asc"]}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        getRowClassName={
          getRowClassName
            ? (params) => getRowClassName(params.row as T) ?? ""
            : undefined
        }
        initialState={{ pinnedColumns: { left: pinnedLeft } }}
        slots={{ toolbar: PlayerStatsToolbar }}
        slotProps={{
          toolbar: {
            percentileMode,
            onPercentileModeChange,
            onOpenFilters,
            filtersOpen,
            filterCount,
            percentileModes,
            efficiencyMode,
            onEfficiencyModeChange,
            csvOptions: {
              fileName: exportFileName,
              getRowsToExport: () => gridExpandedSortedRowIdsSelector(apiRef),
            },
            excelOptions: {
              fileName: exportFileName,
              getRowsToExport: () => gridExpandedSortedRowIdsSelector(apiRef),
            },
          } as any,
        }}
        sx={{
          border: "1px solid #dfe2e6",
          borderTop: "none",
          borderRadius: 0,
          backgroundColor: "#fff",
          // MUI tints headers, pinned columns and the trailing filler with a
          // shared grey by default, which reads as a second, meaningless
          // grouping across the table. The grid is white; structure comes from
          // the rules, not from blocks of tone.
          "--DataGrid-containerBackground": "#ffffff",
          "--DataGrid-pinnedBackground": "#ffffff",
          // Only the unpinned filler is settable here; the pinned one carries the
          // same `inherit !important` from `theme.tsx` as the cells do, and picks
          // up the row colour instead.
          "&& .MuiDataGrid-filler": {
            backgroundColor: "#ffffff",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: "#3f4650",
          },
          "& .MuiDataGrid-columnGroupHeader": {
            backgroundColor: "#fff",
            borderBottom: "1px solid #e6e8eb",
          },
          // The identity group holds an input rather than a label, so its cell
          // gives up the centring and padding the stat groups want, and every
          // wrapper between the cell and the input has to be told to fill.
          //
          // MUI nests four deep here — columnGroupHeader > DraggableContainer >
          // TitleContainer > TitleContainerContent — and missing any one of them
          // leaves the input sized to its intrinsic width instead of the cell.
          [`&& .MuiDataGrid-columnGroupHeader.${IDENTITY_GROUP_CLASS}`]: {
            padding: 0,
            // MUI rings the header that contains focus in `alpha(primary, 0.5)`.
            // The input has its own focus indicator; a red rectangle around the
            // whole block on top of it just reads as an error.
            "&:focus-within, &:focus": { outline: "none" },
          },
          [`&& .MuiDataGrid-columnGroupHeader.${IDENTITY_GROUP_CLASS} .MuiDataGrid-columnHeaderDraggableContainer,
            && .MuiDataGrid-columnGroupHeader.${IDENTITY_GROUP_CLASS} .MuiDataGrid-columnHeaderTitleContainer,
            && .MuiDataGrid-columnGroupHeader.${IDENTITY_GROUP_CLASS} .MuiDataGrid-columnHeaderTitleContainerContent`]:
            { width: "100%", minWidth: 0, padding: 0, overflow: "visible" },
          "& .MuiDataGrid-columnGroupHeader .MuiDataGrid-columnHeaderTitle": {
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: "0.13em",
            color: "#17191c",
          },
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: "2px solid #17191c",
          },
          // The numbers are the content — they carry more weight than the chrome
          // around them.
          "& .MuiDataGrid-cell": {
            fontSize: 13.5,
            fontWeight: 600,
            color: "#17191c",
            // MUI pads cells 10px a side. At ~14 columns that is 280px of the
            // window spent on gutters; 6px still separates them.
            paddingLeft: "6px",
            paddingRight: "6px",
            borderBottom: "1px solid #f1f2f4",
            borderRight: "1px solid #f1f2f4",
            fontVariantNumeric: "tabular-nums",
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#fff",
            borderRight: "1px solid #f1f2f4",
            paddingLeft: "6px",
            paddingRight: "6px",
          },
          // Row backgrounds, never cell backgrounds. `theme.tsx` forces
          // `.MuiDataGrid-cell { background-color: inherit !important }` globally,
          // so any colour set on a cell here is silently dropped — and pinned
          // cells left inheriting a transparent row let the scrolling columns
          // show through them. Colour the row and the pinned cells inherit it.
          "& .MuiDataGrid-row": {
            backgroundColor: "#ffffff",
          },
          // Sort affordance: nothing on hover, a small arrow once a column is
          // actually sorted.
          //
          // MUI reveals the sort icon on hover by giving its container
          // `width: auto`, which widens the header and shifts the label sideways
          // under the cursor — every header twitches as the mouse crosses it. The
          // sorted column keeps its arrow, because that's state worth showing.
          "&& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted):hover .MuiDataGrid-iconButtonContainer":
            { visibility: "hidden", width: 0 },
          "& .MuiDataGrid-sortIcon": { fontSize: 15 },
          "& .MuiDataGrid-row:nth-of-type(even)": {
            backgroundColor: "#fbfbfc",
          },
          // Listed after the zebra rules and matching both, so hover reads on
          // every row. Left to the theme's hover it would tie the zebra rule and
          // lose, making even rows look inert under the cursor.
          "& .MuiDataGrid-row:hover, & .MuiDataGrid-row:nth-of-type(even):hover": {
            backgroundColor: "#eceef1",
          },
          "&& .MuiDataGrid-cell--pinnedLeft": {
            borderRight: "none",
          },
          "&& .MuiDataGrid-columnHeader--pinnedLeft": {
            backgroundColor: "#ffffff",
            borderRight: "none",
          },
          // Career rows on the per-player view: a summary of the seasons above,
          // not another season, so it separates itself from them.
          "& .MuiDataGrid-row.player-stats-career-row": {
            backgroundColor: "#f4f5f7",
          },
          "& .MuiDataGrid-row.player-stats-career-row:hover": {
            backgroundColor: "#eceef1",
          },
          "& .MuiDataGrid-row.player-stats-career-row .MuiDataGrid-cell": {
            borderTop: "1px solid #cfd4d9",
          },
          // Column rules are hairlines within a group and a heavier line at each
          // group boundary, so WAYD / EPM / ON-OFF read as blocks without needing
          // background fills to say so.
          [`&& .MuiDataGrid-cell.${GROUP_END_CLASS}, && .MuiDataGrid-columnHeader.${GROUP_END_CLASS}`]:
            { borderRight: "1px solid #cfd4d9" },
        }}
      />
    </Box>
  );
}
